import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Maximize/Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * User Growth Trend Chart for MBF Dashboard
 * Line chart showing monthly user growth with blue theme
 */
const MBFUserGrowthChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Default data matching the Figma design
    const defaultData = [
        { label: 'Jan', users: 50 },
        { label: 'Feb', users: 100 },
        { label: 'Mar', users: 80 },
        { label: 'Apr', users: 150 },
        { label: 'May', users: 450 },
        { label: 'Jun', users: 600 },
        { label: 'Jul', users: 700 },
        { label: 'Aug', users: 950 },
        { label: 'Sep', users: 1000 },
        { label: 'Oct', users: 1200 },
        { label: 'Nov', users: 1800 },
        { label: 'Dec', users: 2450 },
    ];

    // Transform API data to chart format
    // API returns: { month: 'Jan', year: 2025, users: 38 }
    // Chart expects: { label: 'Jan', users: 38 }
    const transformedData = data.length > 0
        ? data.map(item => ({
            label: item.month || item.label || '',
            users: item.users || 0,
        }))
        : defaultData;

    const chartData = transformedData;
    const labels = chartData.map(item => item.label);
    const values = chartData.map(item => item.users);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const fontSize = isMobile ? 10 : isTablet ? 11 : 12;
            const labelFontSize = isMobile ? 11 : isTablet ? 12 : 14;
            const pointRadius = isMobile ? 3 : isTablet ? 3.5 : 4;
            const pointHoverRadius = isMobile ? 5 : isTablet ? 5.5 : 6;

            const ctx = chartRef.current.getContext('2d');

            // Create gradient fill
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, 'rgba(59, 130, 246, 0.15)');
            gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');

            chartInstance.current = new ChartJS(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Users',
                            data: values,
                            borderColor: '#3b82f6',
                            backgroundColor: gradient,
                            borderWidth: isMobile ? 1.5 : 2,
                            fill: true,
                            tension: 0.3,
                            pointRadius: pointRadius,
                            pointBackgroundColor: '#ffffff',
                            pointBorderColor: '#3b82f6',
                            pointBorderWidth: isMobile ? 1.5 : 2,
                            pointHoverRadius: pointHoverRadius,
                            pointHoverBackgroundColor: '#3b82f6',
                            pointHoverBorderColor: '#ffffff',
                            pointHoverBorderWidth: isMobile ? 1.5 : 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#3b82f6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                            displayColors: false,
                            titleFont: { size: isMobile ? 11 : 12 },
                            bodyFont: { size: isMobile ? 11 : 12 },
                            callbacks: {
                                label: (context) => `Users: ${context.raw.toLocaleString()}`,
                            },
                        },
                        datalabels: {
                            display: (context) => context.active,
                            align: 'top',
                            anchor: 'end',
                            offset: isMobile ? 4 : isTablet ? 6 : 8,
                            color: '#3b82f6',
                            font: {
                                family: '"Roboto", sans-serif',
                                size: labelFontSize,
                                weight: '700',
                            },
                            formatter: (value) => value.toLocaleString(),
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#666666',
                                font: {
                                    family: '"Roboto", sans-serif',
                                    size: fontSize,
                                },
                                maxRotation: isMobile ? 45 : 0,
                                minRotation: isMobile ? 45 : 0,
                            },
                        },
                        y: {
                            min: 0,
                            suggestedMax: Math.max(...values) * 1.2 || 100,
                            ticks: {
                                color: '#666666',
                                font: {
                                    family: '"Roboto", sans-serif',
                                    size: fontSize,
                                },
                                callback: (value) => value.toLocaleString(),
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, isTablet, chartData]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        User Growth Trend
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Chart Area */}
            <Box
                sx={{
                    flex: 1,
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart */}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <canvas ref={chartRef} />
                </Box>

                {/* Legend */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '24px',
                        pt: 1,
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            color: '#666666',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '12px',
                            fontWeight: 400,
                        }}
                    >
                        Months
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box
                            sx={{
                                width: '24px',
                                height: '3px',
                                bgcolor: '#3b82f6',
                                borderRadius: '2px',
                            }}
                        />
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Roboto", sans-serif',
                                fontSize: '12px',
                                fontWeight: 500,
                            }}
                        >
                            Users
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFUserGrowthChart;
